/**
 * Power supply stances — one assessment per supply option, for the declared
 * 24/7 campus load, from site evidence and the Stage 1 findings.
 *
 * Every constraint must hold across the declared load RANGE (the lower edge is
 * used) and cites its evidence. Informational reasons carry `effect: 'viable'`
 * so they never create a favoured or constrained stance on their own. A reason
 * that only records why an option is NOT assessed (no evidence, or regional
 * context that says nothing about this site) carries `effect: 'not-assessed'`
 * and never makes the option read as viable. Thresholds that are platform
 * judgement are labelled `screening-policy`.
 *
 * Grid doctrine: a utility indication, a user-authored indication or posted
 * ATC is documented capacity, not a commitment to serve the load. Only a
 * utility study or an executed agreement covering the load is `favoured`; an
 * indication that covers the load is `viable` and names what remains to be
 * confirmed (load service, import capacity during generation outages,
 * upgrades and standby terms). A shortfall is evidence whatever its basis.
 *
 * Regulatory doctrine (shared with the report prompts'
 * `regulatoryApplicabilityPolicy`): planned MW is not potential to emit. The
 * air rule states the permitting path a nonattainment area or the Ozone
 * Transport Region imposes on a MAJOR source; it never computes tons per year
 * or asserts that a threshold is crossed.
 */
import type {
  PerSourceAvailabilityResolution,
  SourceAvailability,
} from '../powerAvailability/resolvePerSourceAvailability';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { EpaAttainmentData } from '../../services/dataProviders/epaAttainment';
import { epaDesignationAsOf } from '../sed/epaDesignation';
import { describeEpaExportGaps, epaExportGaps } from '../sed/epaExportCoverage';
import { stripNarrativeArtifacts } from '../reportRendering/narrativeArtifacts';
import type {
  OptionStance,
  StanceReason,
  Stage2OptionAssessment,
} from './stage2Types';
import {
  addYears,
  campusLoad,
  datedPhases,
  EPA_DESIGNATION_EVIDENCE_PATH,
  epaContradictedText,
  phaseList,
  phasesBefore,
  powerOptionsNamedIn,
  readEpaNonattainment,
  readEpaOtherNonattainment,
  readNonattainment,
  readOtrMembership,
  type EpaOtherNonattainment,
  type CampusLoad,
  type NonattainmentSignal,
  type OtrMembership,
  type PowerOptionId,
} from './stage2Evidence';

/**
 * Most restrictive effect wins; a favourable reason never outweighs a
 * constraint. Reasons whose effect is 'not-assessed' explain an open option
 * and never make it viable.
 */
export function combineStances(reasons: readonly StanceReason[]): OptionStance {
  if (reasons.some(reason => reason.effect === 'excluded-by-evidence')) return 'excluded-by-evidence';
  if (reasons.some(reason => reason.effect === 'constrained')) return 'constrained';
  if (reasons.some(reason => reason.effect === 'favoured')) return 'favoured';
  if (reasons.some(reason => reason.effect === 'viable')) return 'viable';
  return 'not-assessed';
}

export const POWER_OPTION_LABEL: Readonly<Record<PowerOptionId, string>> = {
  grid: 'Grid import (utility service)',
  'gas-cc': 'On-site gas — combined cycle',
  'gas-sc': 'On-site gas — simple cycle',
  recip: 'On-site gas engines',
  'solar-bess': 'Solar + storage',
  'wind-bess': 'Wind + storage',
  smr: 'Small modular reactor',
  'large-nuclear': 'Large nuclear',
};

const POWER_OPTION_ORDER: readonly PowerOptionId[] = [
  'grid', 'gas-cc', 'gas-sc', 'recip', 'solar-bess', 'wind-bess', 'smr', 'large-nuclear',
];

/** NNSR major-source threshold (tpy NOx or VOC) and offset ratio, 40 CFR 51.165. */
const NNSR_BY_CLASSIFICATION = {
  marginal: { tpy: 100, offsets: '1.1' },
  moderate: { tpy: 100, offsets: '1.15' },
  serious: { tpy: 50, offsets: '1.2' },
  severe: { tpy: 25, offsets: '1.3' },
  extreme: { tpy: 10, offsets: '1.5' },
} as const;

/**
 * Inside the Ozone Transport Region a new source is major at 100 tpy of NOx or
 * 50 tpy of VOC and takes a moderate area's offsets, attainment areas
 * included (CAA §184(b)(2); 40 CFR 51.165(a)(1)(iv)(A)(1)(ii), (A)(2)(iii) and
 * (a)(9)(iii)). A serious, severe or extreme classification sets stricter
 * terms and governs instead.
 */
const OTR_NNSR = { noxTpy: 100, vocTpy: 50, offsets: '1.15' } as const;
const GOVERNS_OVER_OTR: ReadonlySet<NonattainmentSignal['classification']> = new Set(['serious', 'severe', 'extreme']);

/** LBNL median tracking-PV energy density, MWh per acre per year. */
const PV_TRACKING_MWH_PER_ACRE_YEAR = 394;
/** NREL total wind project area, acres per MW (34 ha/MW). */
const WIND_TOTAL_ACRES_PER_MW = 34 * 2.471;
const HOURS_PER_YEAR = 8760;
/** GE Vernova's directional heavy-duty turbine lead time (April 2026). */
const HEAVY_DUTY_TURBINE_LEAD_YEARS = 3;
/** The first North American BWRX-300 targets operation by the end of 2029. */
const FIRST_SMR_OPERATION_CUTOFF = '2030-01-01';
/** Vogtle 3/4: construction began 2013, operation 2023–2024 (about eleven years). */
const LARGE_NUCLEAR_BUILD_YEARS = 11;

export interface PowerStanceInputs {
  intent: ProjectIntent | null | undefined;
  sed: SiteEvidenceDocument | null | undefined;
  availability: PerSourceAvailabilityResolution | null | undefined;
  feasibility: SiteRagReport | null;
  environmental: SiteRagReport | null;
  /** Gross acres of the selected study area, when one is selected. */
  studyAreaAcres: number | null;
  /** Today, supplied by the caller so the rules stay deterministic. */
  asOf: string;
}

function formatMw(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} MW`;
}

function loadPhrase(load: CampusLoad): string {
  return load.basis ? `${load.label} of ${load.basis}` : load.label;
}

/**
 * A favourable screening signal for the grid is capped at 'viable': the
 * screening firm-capacity check and grade are planning screens, not a utility
 * commitment to serve the load. Only a committed capacity detail (study or
 * executed agreement, see `gridReasons`) can favour grid supply.
 */
const GRID_SCREENING_NOT_COMMITMENT = 'A screening check is not a utility commitment to serve the load.';

/** The effect of a favourable screening signal, and the caveat its text carries. */
function favourableScreeningSignal(option: PowerOptionId): { effect: OptionStance; caveat: string | null } {
  return option === 'grid'
    ? { effect: 'viable', caveat: GRID_SCREENING_NOT_COMMITMENT }
    : { effect: 'favoured', caveat: null };
}

function withCaveat(text: string, caveat: string | null): string {
  if (!caveat) return text;
  const trimmed = text.trimEnd();
  return `${trimmed}${/[.!?]$/.test(trimmed) ? '' : '.'} ${caveat}`;
}

/** Stage 1 screening signals: the pathway's named sources and the BTM grades. */
function screeningSignals(option: PowerOptionId, report: SiteRagReport | null): StanceReason[] {
  if (!report) return [];
  const reasons: StanceReason[] = [];
  (report.developmentPathway ?? []).forEach((step, index) => {
    if (!powerOptionsNamedIn(step.powerSource).includes(option)) return;
    const verdict = typeof step.firmCapacityVerdict === 'string' ? step.firmCapacityVerdict : undefined;
    const signal = verdict === 'feasible'
      ? favourableScreeningSignal(option)
      : { effect: verdict === 'infeasible' ? 'constrained' as const : 'viable' as const, caveat: null };
    const cleanSource = stripNarrativeArtifacts(step.powerSource ?? '').trim();
    reasons.push({
      text: withCaveat(`Screening pathway ${step.phase}: “${cleanSource}”${verdict ? ` (screening firm-capacity check: ${verdict})` : ''}.`, signal.caveat),
      basis: 'screening-report',
      effect: signal.effect,
      evidencePath: `report:${report.id}.developmentPathway[${index}]`,
    });
  });
  (report.btmOptions ?? []).forEach((btm, index) => {
    if (!powerOptionsNamedIn(btm.technology).includes(option)) return;
    const signal = btm.impactLevel === 'Green'
      ? favourableScreeningSignal(option)
      : { effect: btm.impactLevel === 'Red' ? 'constrained' as const : 'viable' as const, caveat: null };
    const cleanTechnology = stripNarrativeArtifacts(btm.technology).trim();
    const cleanReason = stripNarrativeArtifacts(btm.reason).trim();
    reasons.push({
      text: withCaveat(`Screening grades “${cleanTechnology}” ${btm.riskLevel.toLowerCase()} risk, ${btm.impactLevel.toLowerCase()} impact: ${cleanReason}`, signal.caveat),
      basis: 'screening-report',
      effect: signal.effect,
      evidencePath: `report:${report.id}.btmOptions[${index}]`,
    });
  });
  return reasons;
}

/**
 * The basis on which documented grid capacity is COMMITTED to the load: a
 * structured capacity detail from a utility study or an executed agreement,
 * on a verified (not user-authored) grid section, whose MW is the figure the
 * availability resolver documented. Anything else is not a commitment.
 */
function committedGridBasis(inputs: PowerStanceInputs, grid: SourceAvailability): 'study' | 'executed' | null {
  if (grid.basis !== 'utility-capacity-detail' || grid.provenance.sectionStatus !== 'verified') return null;
  const detail = inputs.sed?.grid?.data?.capacityDetail;
  if (!detail || detail.mw !== grid.availableMW) return null;
  return detail.basis === 'study' || detail.basis === 'executed' ? detail.basis : null;
}

/** What an uncommitted documented grid figure is, for the stance text. */
function uncommittedGridLabel(grid: SourceAvailability): string {
  if (grid.basis === 'atc-oasis') return 'posted transmission ATC';
  if (grid.provenance.sectionStatus === 'verified-from-context') return 'a user-authored utility indication, not independently verified';
  return 'a utility indication';
}

function gridReasons(inputs: PowerStanceInputs, load: CampusLoad | null): StanceReason[] {
  const reasons: StanceReason[] = [];
  const grid = inputs.availability?.perSource.grid;
  if (grid?.status === 'documented' && grid.availableMW != null) {
    const available = `Documented utility capacity of about ${formatMw(grid.availableMW)}`;
    const committed = committedGridBasis(inputs, grid);
    let text: string;
    let effect: OptionStance;
    if (!load) {
      text = `${available}; the campus load is not declared, so coverage cannot be judged.`;
      effect = 'viable';
    } else if (grid.availableMW >= load.minMW && committed) {
      text = `${available} (${committed === 'executed' ? 'executed agreement' : 'utility study'}) covers the declared campus load (${loadPhrase(load)}).`;
      effect = 'favoured';
    } else if (grid.availableMW >= load.minMW) {
      // An indication is not an entitlement: it never favours grid-only supply.
      text = `${available} would cover the declared campus load (${loadPhrase(load)}), but it is ${uncommittedGridLabel(grid)}, not a commitment to serve the load. Confirm load service, import capacity during generation outages, upgrades and standby terms with the utility.`;
      effect = 'viable';
    } else {
      text = `${available} is below the declared campus load (${loadPhrase(load)}); grid import alone cannot serve the full build.`;
      effect = 'constrained';
    }
    reasons.push({ text, basis: 'site-evidence', effect, evidencePath: 'sed.grid.capacityDetail' });
  } else if (grid?.status === 'proxy' && grid.availableMW != null) {
    reasons.push({
      text: `Only a transmission voltage-class proxy is available (about ${formatMw(grid.availableMW)}); it is not a utility commitment. Confirm capacity with a utility letter.`,
      basis: 'site-evidence',
      effect: 'viable',
      evidencePath: 'sed.grid',
    });
  } else {
    // Undocumented grid sets no stance: grid import is not assessed, not viable.
    reasons.push({
      text: 'No utility capacity is documented. Request a capacity or will-serve letter before relying on grid import.',
      basis: 'site-evidence',
      effect: 'not-assessed',
      evidencePath: 'sed.grid',
    });
  }
  const queue = inputs.intent?.interconnectionStatus?.queueStatus;
  if (queue == null || queue === 'none' || queue === 'pre-application') {
    // Regional context assesses nothing about this site's grid service.
    reasons.push({
      text: 'Regional context: generator interconnection has taken over five years from request to operation in regions with data (LBNL, 2026). Large-load service follows utility-specific processes; confirm the timeline with the utility.',
      basis: 'industry-evidence',
      effect: 'not-assessed',
      sourceIds: ['lbnl-queued-up-2026'],
    });
  }
  return reasons;
}

/**
 * A designation the text fallback read (EPA did not assess the county) says
 * so, so a constrained stance beside the scorecard's "Not verified" is not a
 * contradiction.
 */
const TEXT_FALLBACK_PROVENANCE = ' This designation is read from screening text, not confirmed against the EPA Green Book; verify it before relying on the threshold below.';

const MAJOR_SOURCE_CAVEAT = ' Whether this plant is major depends on its equipment, controls and operating limits, which screening has not fixed.';

/** "the severe area", "the extreme and severe areas" (an unclassified area is "designated"). */
function areaList(classes: ReadonlyArray<NonattainmentSignal['classification']>): string {
  const names = classes.map(c => c ?? 'designated');
  const joined = names.length <= 1 ? (names[0] ?? 'designated') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `the ${joined} area${names.length > 1 ? 's' : ''}`;
}

const withArticle = (word: string): string => `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;

/** Sources behind an EPA-sourced reason; the boundary layer only when it resolved something. */
function epaSourceIds(boundaryResolved: boolean): StanceReason['sourceIds'] {
  return boundaryResolved
    ? ['cfr-40-51-165', 'epa-green-book-data', 'epa-nonattainment-boundaries']
    : ['cfr-40-51-165', 'epa-green-book-data'];
}

/**
 * EPA-7 — EPA's nonattainment-area boundaries place the site point outside
 * every current area of the pollutant (all of them cover only part of the
 * county). Their NNSR terms do not attach at the point, but the effect stays
 * 'constrained' in this change: the point is the parcel centroid, a campus
 * footprint can extend across the boundary, and 40 CFR Part 81, not the GIS
 * layer, is the legal boundary. Relaxing the stance is left to the Stage 2
 * rule owner.
 */
function outsideAreasReason(signal: NonattainmentSignal, inOtr: boolean): StanceReason {
  const outside = signal.epa?.outsideAreas ?? [];
  const top = outside[0]?.classification ?? null;
  const plural = outside.length > 1;
  const pollutant = signal.pollutant === 'pm2.5' ? 'PM2.5' : signal.pollutant === 'pm10' ? 'PM-10' : 'ozone';
  let terms: string;
  if (signal.pollutant === 'pm2.5' || signal.pollutant === 'pm10') {
    terms = top
      ? `the major-source threshold is ${top === 'serious' ? 70 : 100} tpy of ${pollutant}`
      : `the major-source threshold is 100 tpy of ${pollutant} (70 tpy in serious areas)`;
  } else {
    const rule = top ? NNSR_BY_CLASSIFICATION[top] : null;
    terms = !rule
      ? 'major-source thresholds run from 100 tpy (marginal) to 10 tpy (extreme)'
      : inOtr && !GOVERNS_OVER_OTR.has(top)
        ? `the major-source threshold is ${OTR_NNSR.noxTpy} tpy of NOx or ${OTR_NNSR.vocTpy} tpy of VOC with offsets of at least ${OTR_NNSR.offsets}:1`
        : `the major-source threshold is ${rule.tpy} tpy of NOx or VOC with offsets of at least ${rule.offsets}:1`;
  }
  return {
    basis: 'regulation',
    effect: 'constrained',
    evidencePath: signal.evidencePath,
    text: `EPA Green Book NAYRO (${signal.epa!.asOf}) lists this county in ${pollutant} nonattainment${top ? ` (${top})` : ''}. `
      + `EPA's nonattainment-area ${plural ? 'boundaries place' : 'boundary places'} the site point outside ${areaList(outside.map(area => area.classification))}, `
      + `which cover${plural ? '' : 's'} only part of the county, so ${plural ? 'their' : 'its'} nonattainment NSR terms do not attach at that point. `
      + `Until the plant footprint is confirmed outside the boundary in 40 CFR Part 81, this stays a constraint: inside ${areaList([top])} `
      + `a new major source needs nonattainment NSR approval with lowest-achievable emission controls and emission offsets, and ${terms}.`
      + MAJOR_SOURCE_CAVEAT,
    sourceIds: epaSourceIds(true),
  };
}

function airReason(signal: NonattainmentSignal | null, inOtr: boolean): StanceReason | null {
  if (!signal) return null;
  // EPA-7: site-point containment, present exactly when the areas behind the
  // stated class all cover only part of the county.
  const containment = signal.epa?.partOfCounty ? (signal.epa.siteContainment ?? 'unknown') : undefined;
  if (containment === 'outside') return outsideAreasReason(signal, inOtr);
  const outside = signal.epa?.outsideAreas ?? [];
  const base = { basis: 'regulation' as const, effect: 'constrained' as const, evidencePath: signal.evidencePath };
  // The county's listing leads, at its most stringent class, even when the
  // site point lies outside that class's area.
  const countyClass = outside.length > 0 ? outside[0]!.classification : signal.classification;
  const classified = countyClass ? ` (${countyClass})` : '';
  // An EPA-sourced signal names the dataset and its export date and cites it.
  // A part-county area is stated as a fact; it does not change the stance.
  // While containment is unknown and a less stringent area covers the whole
  // county, the site cannot lie outside nonattainment, so the reason gives
  // both thresholds. Once EPA's boundary places the point, only the class
  // that applies there is stated.
  const wholeCounty = containment === 'unknown' ? signal.epa!.wholeCountyArea : undefined;
  const partCounty = (pollutant: string): string => {
    if (!signal.epa) return '';
    const cls = signal.classification ?? 'designated';
    const outsideSentence = outside.length > 0
      ? `EPA's nonattainment-area ${outside.length > 1 ? 'boundaries place' : 'boundary places'} the site point outside ${areaList(outside.map(area => area.classification))}`
      : '';
    const coverOutside = `cover${outside.length > 1 ? '' : 's'} only part of the county`;
    if (!signal.epa.partOfCounty) {
      return outside.length > 0
        ? ` ${outsideSentence}, which ${coverOutside}. All of the county is in ${withArticle(cls)} ${pollutant} nonattainment area.`
        : '';
    }
    if (containment === 'inside') {
      return outside.length > 0
        ? ` ${outsideSentence} and inside the ${cls} area, each covering only part of the county.`
        : ` EPA's nonattainment-area boundary places the site point inside the ${cls} area, which covers only part of the county.`;
    }
    const lead = outside.length > 0 ? ` ${outsideSentence}, which ${coverOutside}.` : '';
    if (!wholeCounty) {
      return lead + (outside.length > 0
        ? ` The ${cls} area also covers only part of the county, so the site may lie outside it.`
        : ' The designated area covers only part of the county, so the site may lie outside it.');
    }
    return `${lead} The ${cls} area covers only part of the county; all of the county is in `
      + `${wholeCounty.classification ? `a ${wholeCounty.classification}` : 'another'} ${pollutant} nonattainment area.`;
  };
  const where = wholeCounty ? `inside the ${signal.classification} area ` : 'here ';
  const reported = (pollutant: 'Ozone' | 'PM2.5' | 'PM-10'): string => (signal.epa
    ? `EPA Green Book NAYRO (${signal.epa.asOf}) lists this county in ${pollutant === 'Ozone' ? 'ozone' : pollutant} nonattainment${classified}.`
      + partCounty(pollutant === 'Ozone' ? 'ozone' : pollutant)
    : `${pollutant} nonattainment is reported for this area${classified}.${TEXT_FALLBACK_PROVENANCE}`);
  const cited = (textSourceIds: StanceReason['sourceIds']): StanceReason['sourceIds'] => (
    signal.epa ? epaSourceIds(containment === 'inside' || outside.length > 0) : textSourceIds);
  if (signal.pollutant === 'pm2.5' || signal.pollutant === 'pm10') {
    // 40 CFR 51.165(a)(1)(iv)(A): 70 tpy of PM2.5/PM-10 in Serious areas,
    // else 100. Owner D03 selects PM-10 Serious as constrained, not Moderate.
    const particulate = signal.pollutant === 'pm10' ? 'PM-10' : 'PM2.5';
    const tpy = signal.classification === 'serious' ? 70 : 100;
    const outsideSeriousPm10 = signal.pollutant === 'pm10' && signal.classification !== 'serious'
      && outside.some(area => area.classification === 'serious');
    return {
      ...base,
      text: `${reported(particulate)} A new major source needs nonattainment NSR approval with lowest-achievable emission controls and emission offsets`
        + (signal.classification
          ? `: ${where}the major-source threshold is ${tpy} tpy of ${particulate}${wholeCounty ? '; elsewhere in the county it is 100 tpy.' : '.'}`
          : `; the ${particulate} major-source threshold is 100 tpy (70 tpy in serious areas).`)
        + (outsideSeriousPm10 ? ' The Moderate PM-10 area is informational; the constraint remains until the plant footprint is confirmed outside the Serious area, where the threshold is 70 tpy.' : '')
        + MAJOR_SOURCE_CAVEAT,
      sourceIds: cited(['cfr-40-51-165']),
    };
  }
  if (signal.pollutant === 'unspecified') {
    return {
      ...base,
      text: 'Nonattainment is reported for this area in screening text, not confirmed against the EPA Green Book, and the statement does not name the pollutant. Confirm the designation in EPA\'s Green Book: for ozone, major-source thresholds run from 100 tpy (marginal) to 10 tpy (extreme), with emission offsets.'
        + MAJOR_SOURCE_CAVEAT,
      sourceIds: ['cfr-40-51-165', 'epa-green-book-data'],
    };
  }
  const rule = signal.classification ? NNSR_BY_CLASSIFICATION[signal.classification] : null;
  const wholeRule = wholeCounty?.classification ? NNSR_BY_CLASSIFICATION[wholeCounty.classification] : null;
  // Inside the OTR a marginal or moderate area takes the region's VOC
  // threshold and offset ratio, in either part of a split county.
  const otrTerms = (classification: NonattainmentSignal['classification']): boolean =>
    inOtr && classification != null && !GOVERNS_OVER_OTR.has(classification);
  const threshold = !rule
    ? null
    : otrTerms(signal.classification)
      ? `${where === 'here ' ? 'here, inside the Ozone Transport Region, ' : `${where.trimEnd()}, which lies in the Ozone Transport Region, `}`
        + `the major-source threshold is ${OTR_NNSR.noxTpy} tpy of NOx or ${OTR_NNSR.vocTpy} tpy of VOC and offsets are at least ${OTR_NNSR.offsets}:1`
      : `${where}the major-source threshold is ${rule.tpy} tpy of NOx or VOC and offsets are at least ${rule.offsets}:1`;
  const elsewhere = !wholeRule
    ? '.'
    : otrTerms(wholeCounty!.classification)
      ? `; elsewhere in the county (${wholeCounty!.classification}, in the Ozone Transport Region) it is ${OTR_NNSR.noxTpy} tpy of NOx or ${OTR_NNSR.vocTpy} tpy of VOC with offsets of at least ${OTR_NNSR.offsets}:1.`
      : `; elsewhere in the county (${wholeCounty!.classification}) it is ${wholeRule.tpy} tpy with offsets of at least ${wholeRule.offsets}:1.`;
  return {
    ...base,
    text: `${reported('Ozone')} A new major source needs nonattainment NSR approval with lowest-achievable emission controls and emission offsets`
      + (threshold ? `: ${threshold}${elsewhere}` : '; major-source thresholds run from 100 tpy (marginal) to 10 tpy (extreme).')
      + MAJOR_SOURCE_CAVEAT,
    sourceIds: cited(['cfr-40-51-165']),
  };
}

/**
 * The Ozone Transport Region's permitting path for on-site combustion: a
 * moderate area's nonattainment NSR anywhere in the region, attainment areas
 * included. Like the ozone rule it never computes potential to emit.
 * `constrained` is the owner's decision (29 September 2026): the region
 * imposes a moderate designation's path, which the ozone rule already
 * constrains. Like any constrained option, combustion stays selectable but
 * drops out of one-click phase proposals (`deriveStage2Decisions`).
 */
function ozoneTransportReason(otr: Extract<OtrMembership, { status: 'inside' }>): StanceReason {
  const where = otr.virginiaLocality
    ? `${otr.place} is one of the ten Virginia localities EPA lists in the Ozone Transport Region (Clean Air Act §184).`
    : `${otr.place} lies wholly in the Ozone Transport Region (Clean Air Act §184).`;
  return {
    text: `${where} There, a new source that emits or could emit at least ${OTR_NNSR.noxTpy} tpy of NOx or ${OTR_NNSR.vocTpy} tpy of VOC is a major source and needs a moderate area's nonattainment NSR approval, `
      + `with lowest-achievable emission controls and emission offsets of at least ${OTR_NNSR.offsets}:1, including in areas designated attainment.`
      + MAJOR_SOURCE_CAVEAT,
    basis: 'regulation',
    effect: 'constrained',
    sourceIds: otr.virginiaLocality
      ? ['usc-42-7511c', 'epa-virginia-otr-2018', 'cfr-40-51-165']
      : ['usc-42-7511c', 'cfr-40-51-165'],
    evidencePath: otr.evidencePath,
    rule: 'ozone-transport',
  };
}

/**
 * True for the Ozone Transport Region reason. It rests on the site's location,
 * not on a designation, so it never stands in for designation evidence.
 */
export function isOzoneTransportReason(reason: StanceReason): boolean {
  return reason.rule === 'ozone-transport';
}

/**
 * One informational reason listing every current nonattainment standard the
 * constrained air reason does not cover. The rule never computes potential to
 * emit, so it states the permitting consequence without a stance change.
 */
function otherNonattainmentReason(
  epa: EpaAttainmentData,
  others: readonly EpaOtherNonattainment[],
  alongsideSignal: boolean,
): StanceReason | null {
  if (others.length === 0) return null;
  // EPA-7: a part-county standard names where EPA's boundaries place the site point, when resolved.
  const partNote = (containment: EpaOtherNonattainment['siteContainment']): string => (
    containment === 'inside' || containment === 'outside'
      ? `part of the county; EPA's boundaries place the site point ${containment} it`
      : 'part of the county');
  const standards = others.map(other => {
    const notes = [...other.classifications, ...(other.partOfCounty ? [partNote(other.siteContainment)] : [])];
    return notes.length > 0 ? `${other.naaqs} (${notes.join('; ')})` : other.naaqs;
  }).join(', ');
  const boundaryResolved = others.some(other => other.siteContainment === 'inside' || other.siteContainment === 'outside');
  // Secondary PM-10 Serious must retain its own constraint even when ozone
  // or PM2.5 takes the primary signal. No PTE or parcel-wide exemption is
  // inferred from a point outside a part-county area.
  const seriousPm10 = others.some(other => /\bPM-?10\b/i.test(other.naaqs)
    && other.classifications.some(classification => /\bserious\b/i.test(classification)));
  return {
    text: `EPA Green Book NAYRO (${epaDesignationAsOf(epa)}) ${alongsideSignal ? 'also lists' : 'lists'} current nonattainment for ${standards}. `
      + `A new major source of ${others.length === 1 ? 'that pollutant' : 'those pollutants'} needs nonattainment NSR approval.`
      + (seriousPm10 ? ' Within the PM-10 Serious area, the major-source threshold is 70 tpy of PM-10. Confirm plant-footprint containment against 40 CFR Part 81; a site point outside the area does not establish the whole plant footprint is outside.' : '')
      + MAJOR_SOURCE_CAVEAT,
    basis: 'regulation',
    effect: seriousPm10 ? 'constrained' : 'viable',
    sourceIds: epaSourceIds(boundaryResolved),
    evidencePath: EPA_DESIGNATION_EVIDENCE_PATH,
  };
}

const EXCERPT_CHARS = 140;

/**
 * Screening text the coverage-backed EPA listing contradicts. EPA wins, but
 * the disagreement stays visible as an informational reason: the text may
 * rest on a revoked standard or a former designation, or on a designation
 * newer than the export. When the text names a standard the export does not
 * include, or dates a designation after it, the reason says so (S4); the
 * effect stays informational.
 */
function airDisagreementReason(epa: EpaAttainmentData, text: NonattainmentSignal): StanceReason {
  const pollutant = text.pollutant === 'ozone' ? '8-hour ozone ' : text.pollutant === 'pm2.5' ? 'PM2.5 ' : '';
  const excerpt = text.text.length > EXCERPT_CHARS ? `${text.text.slice(0, EXCERPT_CHARS - 1).trimEnd()}…` : text.text;
  const outside = describeEpaExportGaps(epaExportGaps(epa, text.text));
  return {
    text: `EPA Green Book NAYRO (${epaDesignationAsOf(epa)}) lists no current ${pollutant}nonattainment for ${epa.county}, ${epa.state}, `
      + (outside
        ? `but the screening text reports nonattainment (“${excerpt}”) under ${outside}, which this export does not cover; a designation under it can post-date the table. `
        : `but the screening text reports nonattainment (“${excerpt}”), for example under a revoked standard or a former designation. `)
      + 'Confirm the designation with the state air agency.',
    basis: 'regulation',
    effect: 'viable',
    sourceIds: ['epa-green-book-data'],
    evidencePath: text.evidencePath,
  };
}

function fuelReasons(inputs: PowerStanceInputs, load: CampusLoad | null): StanceReason[] {
  const reasons: StanceReason[] = [];
  const supply = inputs.intent?.fuelAccess?.supply;
  if (supply === 'firm-contracted') {
    reasons.push({ text: 'The owner records firm-contracted gas supply.', basis: 'owner-input', effect: 'favoured' });
  } else if (supply === 'near-pipeline') {
    reasons.push({ text: 'The owner records the site as near a pipeline; firm transport is not yet contracted.', basis: 'owner-input', effect: 'viable' });
  } else if (supply === 'lateral-required') {
    reasons.push({ text: 'The owner records that a pipeline lateral is required — a separately permitted, long-lead asset.', basis: 'owner-input', effect: 'viable' });
  } else if (supply === 'none') {
    reasons.push({ text: 'The owner records no gas supply path.', basis: 'owner-input', effect: 'constrained' });
  }
  const pool = inputs.availability?.sharedConstraints.find(constraint => constraint.id === 'gas-fuel-pool');
  if (pool) {
    const covered = load ? pool.capMW >= load.minMW : null;
    reasons.push({
      text: `Firm gas deliverability is documented for about ${formatMw(pool.capMW)} of generation${covered === false ? ', below the declared campus load' : ''}.`,
      basis: 'site-evidence',
      effect: covered === false ? 'constrained' : 'viable',
      evidencePath: `availability.${pool.basis}`,
    });
  }
  const fuel = inputs.sed?.fuelSupply?.data;
  if (fuel?.nearestPipelineKm != null) {
    reasons.push({
      text: `Nearest mapped pipeline: ${fuel.nearestPipelineKm.toFixed(1)} km${fuel.nearestPipelineOperator ? ` (${fuel.nearestPipelineOperator})` : ''}. Proximity does not establish firm transport capacity.`,
      basis: 'site-evidence',
      effect: 'viable',
      evidencePath: 'sed.fuelSupply.nearestPipelineKm',
    });
  }
  return reasons;
}

function leadTimeReason(
  inputs: PowerStanceInputs,
  cutoff: string,
  text: (phases: string, cutoff: string) => string,
  sourceIds: StanceReason['sourceIds'],
): StanceReason | null {
  const dated = datedPhases(inputs.intent);
  if (dated.length === 0) return null;
  const early = phasesBefore(dated, cutoff);
  if (early.length === 0) return null;
  return {
    text: text(phaseList(early), cutoff),
    basis: 'industry-evidence',
    // Constrained only when EVERY dated phase falls inside the window; a later
    // phase can still use the option.
    effect: early.length === dated.length ? 'constrained' : 'viable',
    sourceIds,
  };
}

/**
 * True when a phase's exact target date falls inside the option's cited lead
 * time, so the option cannot serve that phase on the evidence. Proposals use
 * it per phase; the campus stance only turns constrained when every dated
 * phase is inside the window.
 */
export function optionMissesPhaseDate(option: PowerOptionId, target: string, asOf: string): boolean {
  const date = target.slice(0, 10);
  if (option === 'gas-cc') return date < addYears(asOf, HEAVY_DUTY_TURBINE_LEAD_YEARS);
  if (option === 'smr') return date < FIRST_SMR_OPERATION_CUTOFF;
  if (option === 'large-nuclear') return date < addYears(asOf, LARGE_NUCLEAR_BUILD_YEARS);
  return false;
}

function landShare(requiredAcres: number, studyAreaAcres: number): number {
  return requiredAcres > 0 ? studyAreaAcres / requiredAcres : 0;
}

function nuclearOutOfScope(intent: ProjectIntent | null | undefined): StanceReason | null {
  return intent?.nuclearInterest === 'no'
    ? { text: 'Nuclear is recorded as not of interest for this project.', basis: 'owner-input', effect: 'viable' }
    : null;
}

/** Assess every supply option. Deterministic for identical inputs. */
export function assessPowerOptions(inputs: PowerStanceInputs): Stage2OptionAssessment[] {
  const load = campusLoad(inputs.intent);
  const researchAirText = {
    text: inputs.sed?.siteResearch?.data?.localEnvironmental?.airQualityRegion,
    evidencePath: 'sed.siteResearch.localEnvironmental.airQualityRegion',
  };
  const textNonattainment = readNonattainment([
    {
      text: inputs.environmental?.airQualityEmissions?.attainmentStatus,
      evidencePath: inputs.environmental ? `report:${inputs.environmental.id}.airQualityEmissions.attainmentStatus` : 'environmental',
    },
    researchAirText,
  ]);
  // A coverage-backed EPA Green Book listing on the SED wins over screening
  // text; the text reader applies only when EPA was not assessed. Research
  // text EPA contradicts is surfaced as an informational reason, never dropped
  // silently. The report's attainment status is not compared: it is generated
  // from this SED, EPA block included, so its echo of the listing ("no current
  // nonattainment ...") is not an independent disagreement.
  const epaDesignations = inputs.sed?.environmental?.data?.naaqsDesignations;
  const epaNonattainment = readEpaNonattainment(epaDesignations);
  const airSignal = epaNonattainment === undefined ? textNonattainment : epaNonattainment;
  const otr = readOtrMembership(inputs.sed);
  const air = airReason(airSignal, otr.status === 'inside');
  // A serious, severe or extreme ozone classification sets stricter terms than
  // the region's, so its own reason governs and the region adds none.
  const ozoneTransport = otr.status === 'inside'
    && !(airSignal?.pollutant === 'ozone' && GOVERNS_OVER_OTR.has(airSignal.classification))
    ? ozoneTransportReason(otr)
    : null;
  const contradictedText = epaContradictedText(epaDesignations, readNonattainment([researchAirText]));
  const airDisagreement = epaDesignations && contradictedText
    ? airDisagreementReason(epaDesignations, contradictedText)
    : null;
  const otherNonattainment = epaDesignations
    ? otherNonattainmentReason(
      epaDesignations,
      readEpaOtherNonattainment(epaDesignations, epaNonattainment?.pollutant ?? null),
      epaNonattainment != null,
    )
    : null;
  const fuel = fuelReasons(inputs, load);
  const heavyDutyCutoff = addYears(inputs.asOf, HEAVY_DUTY_TURBINE_LEAD_YEARS);

  const reasonsFor = (option: PowerOptionId): StanceReason[] => {
    const reasons: StanceReason[] = [];
    switch (option) {
      case 'grid':
        reasons.push(...gridReasons(inputs, load));
        break;
      case 'gas-cc':
      case 'gas-sc':
      case 'recip': {
        reasons.push(...fuel);
        if (air) reasons.push(air);
        if (ozoneTransport) reasons.push(ozoneTransport);
        if (otherNonattainment) reasons.push(otherNonattainment);
        if (airDisagreement) reasons.push(airDisagreement);
        if (option === 'gas-cc') {
          const lead = leadTimeReason(inputs, heavyDutyCutoff,
            (phases, cutoff) => `Heavy-duty gas turbine lead times are about three years before site works, permitting and fuel (GE Vernova, April 2026): ${phases} target operation before ${cutoff} unless turbine slots are already secured.`,
            ['ge-vernova-lead-times-2026']);
          if (lead) reasons.push(lead);
        }
        break;
      }
      case 'solar-bess': {
        reasons.push({
          text: 'Solar produces nothing overnight: as the only supply for a 24/7 load it needs storage for every night and seasonal lows. It fits a hybrid as an energy contributor.',
          basis: 'screening-policy',
          effect: 'constrained',
        });
        if (load && inputs.studyAreaAcres != null && inputs.studyAreaAcres > 0) {
          const required = (load.minMW * HOURS_PER_YEAR) / PV_TRACKING_MWH_PER_ACRE_YEAR;
          const share = landShare(required, inputs.studyAreaAcres);
          reasons.push({
            text: `Matching the campus's annual energy (${formatMw(load.minMW)} around the clock) with tracking PV needs about ${Math.round(required).toLocaleString('en-US')} acres at the LBNL median of ${PV_TRACKING_MWH_PER_ACRE_YEAR} MWh/acre/year; the study area is ${Math.round(inputs.studyAreaAcres).toLocaleString('en-US')} acres (${Math.round(share * 100)}%), before land for the campus itself.`,
            basis: 'industry-evidence',
            effect: share < 1 ? 'constrained' : 'viable',
            sourceIds: ['lbnl-pv-land-2022'],
            evidencePath: 'site.studyArea',
          });
        }
        const ghi = inputs.sed?.solar?.data?.ghi;
        if (typeof ghi === 'number' && Number.isFinite(ghi) && ghi > 0) {
          reasons.push({ text: `Solar resource: GHI ${ghi.toFixed(2)} kWh/m²/day.`, basis: 'site-evidence', effect: 'viable', evidencePath: 'sed.solar.ghi' });
        }
        break;
      }
      case 'wind-bess': {
        reasons.push({
          text: 'Wind output varies with the weather: as the only supply for a 24/7 load it needs firming. It fits a hybrid as an energy contributor.',
          basis: 'screening-policy',
          effect: 'constrained',
        });
        if (load && inputs.studyAreaAcres != null && inputs.studyAreaAcres > 0) {
          const hostableMw = inputs.studyAreaAcres / WIND_TOTAL_ACRES_PER_MW;
          reasons.push({
            text: `At about 84 acres of total project area per MW (NREL), the ${Math.round(inputs.studyAreaAcres).toLocaleString('en-US')}-acre study area could host roughly ${formatMw(hostableMw)} of wind nameplate, against ${loadPhrase(load)} of demand.`,
            basis: 'industry-evidence',
            effect: hostableMw < load.minMW ? 'constrained' : 'viable',
            sourceIds: ['nrel-wind-land-2009'],
            evidencePath: 'site.studyArea',
          });
        }
        const wind = inputs.sed?.wind?.data;
        if (wind && Number.isFinite(wind.windSpeed100m) && wind.windSpeed100m > 0) {
          reasons.push({
            text: `Wind resource: ${wind.windSpeed100m.toFixed(1)} m/s at 100 m${Number.isFinite(wind.capacityFactor) && wind.capacityFactor > 0 ? `, modeled capacity factor ${Math.round(wind.capacityFactor * 100)}%` : ''}.`,
            basis: 'site-evidence',
            effect: 'viable',
            evidencePath: 'sed.wind',
          });
        }
        break;
      }
      case 'smr': {
        const outOfScope = nuclearOutOfScope(inputs.intent);
        if (outOfScope) return [outOfScope];
        const lead = leadTimeReason(inputs, FIRST_SMR_OPERATION_CUTOFF,
          (phases) => `The first North American BWRX-300 targets commercial operation by the end of 2029 and the first US unit applied for its construction permit in 2025 (DOE): ${phases} target operation before 2030.`,
          ['doe-clinch-river-smr-2025']);
        reasons.push(lead ?? {
          text: 'No US SMR is yet in commercial operation; licensing, vendor and schedule risk are design-specific.',
          basis: 'industry-evidence',
          effect: 'viable',
          sourceIds: ['doe-clinch-river-smr-2025'],
        });
        break;
      }
      case 'large-nuclear': {
        const outOfScope = nuclearOutOfScope(inputs.intent);
        if (outOfScope) return [outOfScope];
        const lead = leadTimeReason(inputs, addYears(inputs.asOf, LARGE_NUCLEAR_BUILD_YEARS),
          (phases, cutoff) => `The most recent US large reactors (Vogtle 3 and 4) took about eleven years from construction start in 2013 to operation in 2023–2024 (EIA): ${phases} target operation before ${cutoff}.`,
          ['eia-vogtle-2024']);
        reasons.push(lead ?? {
          text: 'The most recent US large reactors (Vogtle 3 and 4) took about eleven years from construction start to operation (EIA).',
          basis: 'industry-evidence',
          effect: 'viable',
          sourceIds: ['eia-vogtle-2024'],
        });
        break;
      }
      default: {
        const exhaustive: never = option;
        void exhaustive;
      }
    }
    reasons.push(...screeningSignals(option, inputs.feasibility));
    return reasons;
  };

  return POWER_OPTION_ORDER.map(option => {
    const reasons = reasonsFor(option);
    const outOfScope = (option === 'smr' || option === 'large-nuclear') && inputs.intent?.nuclearInterest === 'no';
    return {
      optionId: option,
      label: POWER_OPTION_LABEL[option],
      stance: outOfScope ? 'not-assessed' : combineStances(reasons),
      reasons,
    };
  });
}
